
const Regex = Object.freeze({
  /**
   * Matches an base name with an optional collision postfix (ie. "(1)")
   * and captures the base name and collision postfix value.
   * @example
   * "some tag" -> ["some tag"]
   * "blue input (1)" -> ["blue input", "1"]
   */
  BASE_NAME_AND_COLLISION_COUNT_POSTFIX: /^(.+?)(?: *\((\d+)\))?$/,
});


/**
 * Computes the next name for a given name string, adding a postfix if encountering a collision
  * @example
 * currentName: 'foo', existingNames: {'bar'}, result: 'foo
 * currentName: 'foo', existingNames: {'foo', 'bar'}, result: 'foo (1)
 */
export function computeNextName(currentName: string, existingNames: Set<string>): string {
  if (!hasNameCollision(currentName, existingNames)) {
    return currentName;
  }

  const baseName = extractBaseName(currentName);

  // find all names that have the same base name (without the collision postfix)
  const collidingNames = [...existingNames]
    .filter(name => {
      const collidingBaseName = extractBaseName(name);
      return collidingBaseName === baseName;
    });

  /**
   * sort colliding names in ASC order.
   * naive string comparison would not work - eg. "12" < "2" (1 is lexically smaller than 2)
   */
  const collidingNamesInAscOrder = collidingNames.sort((a, b) => {
    // defensive assignment to 0
    const numericCollisionValueA = extractNameCollisionPostfixValue(a) ?? 0;
    const numericCollisionValueB = extractNameCollisionPostfixValue(b) ?? 0;

    return numericCollisionValueA - numericCollisionValueB;
  });

  const largestPostfixCollisionName = collidingNamesInAscOrder.at(-1) ?? '';
  const largestCollisionValue = extractNameCollisionPostfixValue(largestPostfixCollisionName) ?? 0;

  return `${baseName} (${largestCollisionValue + 1})`;
}

/**
 * Extracts the base name without collision postfix (if present)
 * @example
 * "action name (3)" -> "action name"
 */
function extractBaseName(name: string): string {
  const matches = name.match(Regex.BASE_NAME_AND_COLLISION_COUNT_POSTFIX);
  if (!matches) {
    return name;
  }
  return matches[1].trim();
}

/**
 * Extracts the numeric value (if present) of a collision postfix in a string
 * @example
 * "action name" -> null
 * "action name (3)" -> 3
 */
function extractNameCollisionPostfixValue(name: string): number | null {
  const matches = name.match(Regex.BASE_NAME_AND_COLLISION_COUNT_POSTFIX);
  if (!matches) {
    return null;
  }
  const collisionPostfixValue = matches[2];

  // name string had no collision postfix
  if (!collisionPostfixValue) {
    return null;
  }


  const numericCollisionPostfixValue = Number(collisionPostfixValue);
  return isNaN(numericCollisionPostfixValue) ? null : numericCollisionPostfixValue;
}


export function hasNameCollision(currentName: string, existingNames: Set<string>): boolean {
  return existingNames.has(currentName);
}
